import { Link, Navigate, Outlet, useRouterState } from '@tanstack/react-router';
import { ChevronsLeft, Menu, Search, X } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { can, useMe } from './auth.tsx';
import { ErrorBox, Loading } from './components/ui.tsx';
import { locate, visibleAreas } from './navigation.ts';
import { PlatformTheme, PRESENTATION, useActiveShell, usePlatformLook } from './platform.tsx';
import { ReadingPreferences } from './reading.tsx';
import { PresentationPage } from './pages/Presentation.tsx';
import { ForcedPasswordChange } from './pages/ChangePassword.tsx';
import { Breadcrumbs } from './shell/Breadcrumbs.tsx';
import { AreaColumn, AreaRail } from './shell/DualShell.tsx';
import { MfaNotice } from './shell/MfaNotice.tsx';
import { MobileDrawer } from './shell/MobileDrawer.tsx';
import { AreaGrid, BottomBar } from './shell/MobileNav.tsx';
import { ScreenSearch } from './shell/ScreenSearch.tsx';
import { CollapsedRail, Sidebar } from './shell/Sidebar.tsx';
import { TopBar } from './shell/TopBar.tsx';
import { FocusBar } from './shell/FocusBar.tsx';
import { InspectorPanel } from './shell/InspectorPanel.tsx';
import { AreaTiles, LauncherBar } from './shell/Launcher.tsx';
import { SheetBar } from './shell/SheetBar.tsx';
import { ShellProvider } from './shell/slots.tsx';
import { WorkTabsBar } from './shell/WorkTabs.tsx';

const COLLAPSED_KEY = 'app.sidebarCollapsed';

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(COLLAPSED_KEY) === '1';
  } catch {
    return false;
  }
}

function saveCollapsed(value: boolean) {
  try {
    localStorage.setItem(COLLAPSED_KEY, value ? '1' : '0');
  } catch {
    // navegador sem armazenamento: só não lembra a escolha
  }
}

/**
 * Moldura das telas logadas. No computador, a do layout do estilo (ADR 0128): uma moldura para
 * cada um dos nove layouts do Estúdio. No celular, a faixa com o logo e o menu
 * escolhido: gaveta lateral, barra embaixo ou grade de áreas. As peças ficam em `shell/`.
 */
export function AppLayout() {
  const { t } = useTranslation();
  const me = useMe();
  const look = usePlatformLook();
  const { shell, mobileNav, options } = useActiveShell();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  // Guarda a página pedida antes de mandar para o login (o endereço muda durante o redirecionamento).
  const [requested] = useState(pathname);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  // Departamento escolhido na faixa recolhida, para a lateral abrir nele.
  const [expandTo, setExpandTo] = useState<string>();
  // Área escolhida no trilho (Trilho e coluna); sem escolha, a da tela aberta.
  const [railArea, setRailArea] = useState<string>();
  const [searchOpen, setSearchOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const menuButton = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Mudou de tela: o menu do celular fecha e o trilho volta a mostrar a área da tela aberta.
  const [menuPath, setMenuPath] = useState(pathname);
  if (menuPath !== pathname) {
    setMenuPath(pathname);
    if (mobileOpen) setMobileOpen(false);
    if (railArea) setRailArea(undefined);
  }

  const areas = useMemo(() => visibleAreas((p) => can(me.data, p)), [me.data]);

  if (me.isPending)
    return (
      <div className="p-8">
        <Loading />
      </div>
    );
  if (me.isError)
    return (
      <div className="p-8">
        <ErrorBox error={me.error} />
      </div>
    );
  if (!me.data) {
    // Quem chega no endereço do sistema sem ter entrado vê a apresentação, se houver (ADR 0118).
    if (requested === '/' && PRESENTATION) return <PresentationPage />;
    return <Navigate to="/login" search={{ returnTo: requested }} />;
  }
  if (me.data.mustChangePassword) return <ForcedPasswordChange />;

  const setSaved = (value: boolean) => {
    saveCollapsed(value);
    setCollapsed(value);
  };
  const closeMobile = () => {
    setMobileOpen(false);
    menuButton.current?.focus();
  };
  const openSearch = () => setSearchOpen(true);
  // A coluna do Trilho e coluna: a área escolhida no trilho ou a da tela aberta. As áreas do rodapé
  // (Configuração) têm o próprio submenu na página: a coluna repetiria o mesmo menu, então some.
  const chosenArea =
    areas.find((a) => a.id === railArea) ?? locate(areas, pathname).area ?? areas[0];
  const columnArea = chosenArea && !chosenArea.bottom ? chosenArea : undefined;

  const drawerSidebar = (
    <Sidebar
      areas={areas}
      me={me.data}
      idPrefix="drawer"
      onNavigate={() => setMobileOpen(false)}
      onSearch={() => {
        setMobileOpen(false);
        setSearchOpen(true);
      }}
      headerAction={
        <button
          type="button"
          onClick={closeMobile}
          aria-label={t('nav.close')}
          className="flex size-11 items-center justify-center rounded-md text-ink"
        >
          <X size={22} aria-hidden />
        </button>
      }
    />
  );

  const topShell = ['top', 'tabs', 'launcher', 'focus', 'sheet'].includes(shell);
  const statusBar = shell === 'tabs' && options.statusBar === 'show';
  const content = (
    <>
      {shell !== 'launcher' && shell !== 'focus' && <Breadcrumbs areas={areas} />}
      <MfaNotice />
      {shell === 'launcher' && pathname === '/' && (
        <AreaTiles areas={areas} style={options.tiles} />
      )}
      <Outlet />
    </>
  );

  // relative + overflow-hidden: nada (nem texto escondido para leitor de tela) faz a página inteira
  // rolar; quem rola é cada coluna. As peças antes do <main> só aparecem ou somem no lugar delas:
  // abrir, recolher ou fechar o menu nunca desmonta a tela aberta.
  return (
    <ShellProvider shell={shell} options={options}>
      <div
        className={`relative flex min-h-dvh flex-col md:h-dvh md:overflow-hidden ${
          topShell ? '' : 'md:flex-row'
        }`}
      >
        <PlatformTheme />
        <ReadingPreferences />
        {/* Celular: faixa no topo com o logo e o botão do menu (na barra embaixo, só a busca). */}
        <header className="ui-sidebar flex items-center justify-between border-b border-rule px-4 py-2 md:hidden">
          <Link to="/" aria-label={t('nav.homeBrand')}>
            <img src={look.logoUrl} alt="" className="h-6 w-auto max-w-40 object-contain" />
          </Link>
          <span className="flex items-center gap-2">
            {mobileNav === 'bottom' ? (
              <button
                type="button"
                onClick={openSearch}
                aria-label={t('nav.search')}
                className="flex size-11 items-center justify-center rounded-md text-ink"
              >
                <Search size={20} aria-hidden />
              </button>
            ) : (
              <button
                ref={menuButton}
                type="button"
                onClick={() => setMobileOpen(true)}
                aria-expanded={mobileOpen}
                aria-haspopup="dialog"
                aria-label={t('nav.openMenu')}
                className="flex size-11 items-center justify-center rounded-md text-ink"
              >
                <Menu size={22} aria-hidden />
              </button>
            )}
          </span>
        </header>
        {mobileOpen &&
          (mobileNav === 'grid' ? (
            <MobileDrawer onClose={closeMobile} side="bottom">
              <AreaGrid
                areas={areas}
                onNavigate={() => setMobileOpen(false)}
                onClose={closeMobile}
              />
            </MobileDrawer>
          ) : (
            <MobileDrawer onClose={closeMobile}>{drawerSidebar}</MobileDrawer>
          ))}

        {shell === 'top' && <TopBar areas={areas} me={me.data} onSearch={openSearch} />}
        {shell === 'tabs' && (
          <WorkTabsBar areas={areas} me={me.data} onSearch={openSearch} statusBar={statusBar} />
        )}
        {shell === 'launcher' && <LauncherBar areas={areas} me={me.data} onSearch={openSearch} />}
        {shell === 'focus' && (
          <FocusBar
            areas={areas}
            me={me.data}
            onSearch={openSearch}
            links={options.topLinks === 'show'}
          />
        )}
        {shell === 'sheet' && (
          <SheetBar
            areas={areas}
            me={me.data}
            onSearch={openSearch}
            compact={options.ribbon === 'compact'}
          />
        )}
        {(shell === 'classic' || shell === 'inspector') && (
          <aside
            className={`ui-sidebar relative hidden shrink-0 border-r border-rule md:flex md:flex-col ${
              collapsed ? 'w-16' : 'w-68'
            }`}
          >
            {collapsed ? (
              <CollapsedRail
                areas={areas}
                onSearch={openSearch}
                onExpand={(areaId) => {
                  setExpandTo(areaId);
                  setSaved(false);
                }}
              />
            ) : (
              <Sidebar
                areas={areas}
                me={me.data}
                idPrefix="side"
                initialArea={expandTo}
                onSearch={openSearch}
                headerAction={
                  <button
                    type="button"
                    onClick={() => {
                      setExpandTo(undefined);
                      setSaved(true);
                    }}
                    aria-label={t('nav.collapse')}
                    title={t('nav.collapse')}
                    className="rounded-md border border-control-border p-1.5 text-muted hover:text-ink"
                  >
                    <ChevronsLeft size={14} aria-hidden />
                  </button>
                }
              />
            )}
          </aside>
        )}
        {(shell === 'dual' || shell === 'split') && (
          <div className="hidden shrink-0 md:flex">
            <AreaRail
              areas={areas}
              selected={shell === 'dual' ? railArea : undefined}
              onSelect={setRailArea}
              onSearch={openSearch}
              linkAreas={shell === 'split'}
            />
            {shell === 'dual' && columnArea && <AreaColumn area={columnArea} />}
          </div>
        )}

        {/* *:shrink-0: em janela baixa quem rola é a página; os blocos (inclusive a tabela) nunca
            encolhem até virar uma caixinha com rolagem própria. Com a barra embaixo (celular) ou a
            barra de situação (abas de trabalho), o fim da tela fica acima dela. */}
        <main
          className={`relative flex min-w-0 flex-1 flex-col gap-6 overflow-y-auto px-4 py-6 *:shrink-0 md:px-8 ${
            mobileNav === 'bottom' ? 'pb-24 md:pb-6' : ''
          } ${statusBar ? 'md:pb-12' : ''}`}
        >
          {shell === 'focus' ? (
            <div
              className={`mx-auto flex w-full flex-col gap-6 ${
                options.contentWidth === 'wide' ? 'max-w-[1120px]' : 'max-w-[880px]'
              }`}
            >
              {content}
            </div>
          ) : options.pageWidth === 'contained' ? (
            <div className="mx-auto flex w-full max-w-[1200px] flex-col gap-6">{content}</div>
          ) : (
            content
          )}
        </main>
        {shell === 'inspector' && <InspectorPanel />}

        {mobileNav === 'bottom' && <BottomBar areas={areas} onMore={() => setMobileOpen(true)} />}
        {searchOpen && <ScreenSearch areas={areas} onClose={() => setSearchOpen(false)} />}
      </div>
    </ShellProvider>
  );
}
